<template>
  <div class="rotation-order">
    <div
      ref="rotationGrid"
      class="rotation-grid"
    >
    <div v-for="position in displayPositions" :key="position.id" :class="['position', 'position-' + position.id]">
      <div
        class="circle"
        :class="{
          'setter-circle': isSetterNumber(position.displayNumber),
          'libero-circle': isLiberoNumber(position.displayNumber)
        }"
        @click="substitutePlayer(position)"
      >
        <span class="position-number">{{ position.id }}</span>
        <span class="role-code">{{ position.roleCode }}</span>
        <div class="player">
          <span
            class="player-number"
            :class="{
              'front-row': isFrontRow(position.id)
            }"
          >
            {{ position.displayNumber }}
          </span>
          <span class="player-name" hidden>{{ position.player.name }}</span>
        </div>
      </div>
    </div>
    <div v-if="liberoPlayer" class="position libero-position" @click="handleLiberoCircleClick">
      <div class="circle" :class="{ 'libero-circle': !isLiberoOnCourt }">
        <span class="position-number">{{ isLiberoOnCourt ? replacedPlayerRoleCode : 'L' }}</span>
        <span v-if="isLiberoOnCourt && replacedPlayerRoleCode" class="role-code">{{ replacedPlayerRoleCode }}</span>
        <span v-else class="role-code">L</span>
        <div class="player">
          <span class="player-number">{{ liberoDisplayNumber }}</span>
          <span class="player-name" hidden>{{ liberoPlayer.name }}</span>
        </div>
      </div>
    </div>
    </div>
    <div v-if="showSubstitutionModal" class="modal-backdrop" @click="cancelSubstitution">
      <div class="substitution-modal" :style="modalStyle" @click.stop>
        <h3>Select a player to sub on</h3>
        <ul class="player-list">
          <li v-for="item in availablePlayers" :key="item.player.id" @click="confirmSubstitution(item)">
            <span class="player-number">{{ item.player.number }}</span>
            <span class="player-name">{{ item.player.name }}</span>
          </li>
        </ul>
        <button
          v-if="showSetterSwitchOption"
          type="button"
          class="switch-role-btn"
          @click="switchSetterOppositeRoles"
        >
          Switch back/front setter
        </button>
        <button @click="cancelSubstitution">Cancel</button>
      </div>
    </div>
  </div>
</template>

<script>
import { store } from '../store.js';

export default {
  name: 'RotationOrder',
  props: {
    currentPoint: Object
  },
  data() {
    return {
      showSubstitutionModal: false,
      selectedPosition: null,
      availablePlayers: [],
      modalStyle: {},
      showSetterSwitchOption: false
    };
  },
  computed: {
    currentLibero() {
      return store.currentLibero;
    },
    positions() {
      // Order positions counterclockwise from bottom right
      const getEntry = (rotationPosition) => {
        const slot = store.currentLineup.find(p => p.rotationPosition === rotationPosition) || {};
        return {
          id: rotationPosition,
          player: slot.player || {},
          roleCode: slot.roleCode || ''
        };
      };
      const positions = [
        getEntry(4),
        getEntry(3),
        getEntry(2),
        getEntry(5),
        getEntry(6),
        getEntry(1)
      ];
      // console.log('Computed positions:', positions);
      return positions;
    },
    displayPositions() {
      const onCourt = this.currentPoint && Array.isArray(this.currentPoint.currentOnCourtNumbers)
        ? this.currentPoint.currentOnCourtNumbers
        : null;
      return this.positions.map(position => {
        const displayNumber = onCourt && onCourt[position.id - 1] ? onCourt[position.id - 1] : position.player.number;
        const isLiberoHere = this.liberoNumber && displayNumber === this.liberoNumber && displayNumber !== position.player.number;
        return {
          ...position,
          displayNumber,
          roleCode: isLiberoHere ? 'L' : position.roleCode
        };
      });
    },
    replacedPlayerRoleCode() {
      if (!this.isLiberoOnCourt) return '';
      const onCourt = this.currentPoint.currentOnCourtNumbers || [];
      const pos = this.positions.find(p => onCourt[p.id - 1] === this.liberoNumber && p.player.number !== this.liberoNumber);
      return pos ? pos.roleCode : '';
    },
    liberoPlayer() {
      return this.currentLibero;
    },
    liberoNumber() {
      return this.currentPoint && this.currentPoint.currentLiberoNumber
        ? this.currentPoint.currentLiberoNumber
        : (this.currentLibero ? this.currentLibero.number : null);
    },
    isLiberoOnCourt() {
      if (!this.liberoNumber || !this.currentPoint) {
        return false;
      }
      const onCourt = this.currentPoint.currentOnCourtNumbers || [];
      const base = this.currentPoint.currentLineupNumbers || [];
      return onCourt.includes(this.liberoNumber) && !base.includes(this.liberoNumber);
    },
    liberoDisplayNumber() {
      if (!this.isLiberoOnCourt) {
        return this.liberoNumber;
      }
      const onCourt = this.currentPoint.currentOnCourtNumbers || [];
      const base = this.currentPoint.currentLineupNumbers || [];
      const liberoIndex = onCourt.findIndex(number => number === this.liberoNumber);
      if (liberoIndex === -1) {
        return this.liberoNumber;
      }
      return base[liberoIndex] || this.liberoNumber;
    }
  },
  methods: {
    substitutePlayer(position) {
      if (position.id === 'libero') {
        return;
      }
      if (this.isLiberoOnCourt && this.liberoPlayer && position.displayNumber === this.liberoPlayer.number) {
        this.openLiberoSwapModal();
        return;
      }
      this.showSetterSwitchOption = this.isSetterNumber(position.displayNumber);
      this.selectedPosition = position;
      // console.log('Selected position:', this.selectedPosition);

      // console.log('Store current lineup:', store.currentLineup);
      // console.log('Current positions:', this.positions);

      const rosterPool = store.match.roster.filter(player => player && player.playerType !== 'libero');

      this.availablePlayers = rosterPool.filter(p => {
        const isAvailable = !this.positions.some(pos => pos.player.id === p.id)
          && (!this.currentLibero || this.currentLibero.id !== p.id);
        // console.log(`Checking player ${p.name} (ID: ${p.id}): ${isAvailable}`);
        return isAvailable;
      }).map(player => ({ player }));

      // console.log('Available players:', this.availablePlayers);

      this.showSubstitutionModal = true;
      this.$nextTick(() => this.updateModalPosition());
    },
    openLiberoSubModal() {
      this.showSetterSwitchOption = false;
      if (!this.liberoPlayer || this.isLiberoOnCourt) {
        return;
      }
      const backRowPositions = [1, 5, 6];
      const servingRestricted = this.currentPoint && this.currentPoint.startPoint === 'Serve';
      const candidates = this.positions.filter(position => backRowPositions.includes(position.id));
      this.availablePlayers = candidates
        .filter(position => !(servingRestricted && position.id === 1))
        .map(position => ({ player: position.player, positionId: position.id }));

      if (!this.availablePlayers.length) {
        return;
      }
      this.selectedPosition = { id: 'libero', action: 'sub-in' };
      this.showSubstitutionModal = true;
      this.$nextTick(() => this.updateModalPosition());
    },
    openLiberoSwapModal() {
      this.showSetterSwitchOption = false;
      if (!this.liberoPlayer || !this.isLiberoOnCourt) {
        return;
      }
      const onCourt = this.currentPoint ? this.currentPoint.currentOnCourtNumbers || [] : [];
      const availableLibero = store.match.roster
        .filter(player => player && player.playerType === 'libero')
        .filter(player => player.id !== this.liberoPlayer.id && !onCourt.includes(player.number));
      this.availablePlayers = availableLibero.map(player => ({ player }));
      if (!this.availablePlayers.length) {
        return;
      }
      this.selectedPosition = { id: 'libero', action: 'swap' };
      this.showSubstitutionModal = true;
      this.$nextTick(() => this.updateModalPosition());
    },
    updateModalPosition() {
      const grid = this.$refs.rotationGrid;
      if (!grid) {
        return;
      }
      const rect = grid.getBoundingClientRect();
      const rightOffset = Math.max(0, window.innerWidth - rect.right);
      this.modalStyle = {
        position: 'fixed',
        top: `${rect.bottom + 10}px`,
        right: `${rightOffset}px`,
        zIndex: 1000
      };
    },
    handleLiberoCircleClick() {
      if (!this.isLiberoOnCourt) {
        this.openLiberoSubModal();
        return;
      }
      this.$emit('libero-swap-back');
    },
    confirmSubstitution(item) {
      let substitutedPlayer
      
      if (this.selectedPosition.id === 'libero') {
        if (this.selectedPosition.action === 'swap' && item.player) {
          substitutedPlayer = this.liberoPlayer;
          this.$emit('libero-swap', { substitutedPlayer, newLibero: item.player });
        } else if (item.positionId && this.currentLibero) {
          substitutedPlayer = item.player;
          this.$emit('libero-sub-in', { substitutedPlayer, positionId: item.positionId });
        }
      } else {
        const playerIndex = store.currentLineup.findIndex(p => p.rotationPosition === this.selectedPosition.id);
        if (playerIndex !== -1) {
          substitutedPlayer = store.currentLineup[playerIndex].player;
          store.currentLineup[playerIndex].player = item.player;
        }
      }

      // Emit an event to notify the parent component of the substitution
      if (this.selectedPosition.id !== 'libero') {
        this.$emit('player-substituted', { substitutedPlayer, newPlayer: item.player });
      }

      this.showSubstitutionModal = false;
      this.selectedPosition = null;
      this.availablePlayers = [];
      this.showSetterSwitchOption = false;
    },
    switchSetterOppositeRoles() {
      const setterIndex = store.currentLineup.findIndex(item => item && item.roleCode === 'S');
      const oppositeIndex = store.currentLineup.findIndex(item => item && item.roleCode === 'OP');
      if (setterIndex === -1 || oppositeIndex === -1) {
        const fallbackSetterIndex = store.currentLineup.findIndex(item => item && item.player?.role === 'setter');
        const fallbackOppositeIndex = store.currentLineup.findIndex(item => item && item.player?.role === 'opposite');
        if (fallbackSetterIndex === -1 || fallbackOppositeIndex === -1) {
          return;
        }
        const setterEntry = store.currentLineup[fallbackSetterIndex];
        const oppositeEntry = store.currentLineup[fallbackOppositeIndex];
        setterEntry.roleCode = 'OP';
        oppositeEntry.roleCode = 'S';
        store.currentLineup = store.currentLineup.slice();
        this.$emit('update-starting-players', store.currentLineup, null, store.currentLibero);
        this.showSubstitutionModal = false;
        this.selectedPosition = null;
        this.availablePlayers = [];
        this.showSetterSwitchOption = false;
        return;
      }
      const setterEntry = store.currentLineup[setterIndex];
      const oppositeEntry = store.currentLineup[oppositeIndex];

      setterEntry.roleCode = 'OP';
      oppositeEntry.roleCode = 'S';
      store.currentLineup = store.currentLineup.slice();

      this.$emit('update-starting-players', store.currentLineup, null, store.currentLibero);

      this.showSubstitutionModal = false;
      this.selectedPosition = null;
      this.availablePlayers = [];
      this.showSetterSwitchOption = false;
    },
    isFrontRow(positionId) {
      return [2, 3, 4].includes(positionId);
    },
    isSetterNumber(number) {
      const setter = store.currentLineup.find(item => item && item.roleCode === 'S');
      const setterNumber = setter && setter.player ? setter.player.number : null;
      if (setterNumber === null || setterNumber === undefined) {
        return false;
      }
      return setterNumber === number;
    },
    isLiberoNumber(number) {
      return this.liberoPlayer && number === this.liberoPlayer.number && this.isLiberoOnCourt;
    },
    cancelSubstitution() {
      this.showSubstitutionModal = false;
      this.selectedPosition = null;
      this.showSetterSwitchOption = false;
    }
  },
  mounted() {
    // console.log('store.currentLineup on RotationOrder mounted', store.currentLineup);
  }
};
</script>

<style scoped lang="scss">
.rotation-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 10px;
  justify-items: center;
  align-items: center;
  width: max-content;
  height: max-content;
  margin: 10px auto;
  transition: right 0.3s ease;
}

.position {
  display: flex;
  justify-content: center;
  align-items: center;
}

.circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  color: var(--grey-900);
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  font-size: 14px;
  font-weight: bold;
  position: relative;

  &:hover {
    cursor: pointer;
    background-color: var(--grey-100);
  }

  &.setter-circle {
    .player {
      .player-number {
        background-color: var(--grey-800);
        border-color: var(--grey-800);
        color: var(--white);
      }
    }
  }
  &.libero-circle {
    .player {
      .player-number {
        border-color: var(--red-400);
        color: var(--red-400);
      }
    }
  }
  .position-number {
    font-size: 25px;
    color: var(--grey-100);
    font-weight: 900;
    position: absolute;
    z-index: 0;
    top: -6px;
    right: -1px;
    display: none;
  }
  .role-code {
    position: absolute;
    bottom: 2px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 8px;
    font-weight: 700;
    color: var(--grey-200);
    z-index: 2;
  }
  .player {
    z-index: 1;
    display: flex;
    gap: 1px;
    flex-direction: column;
    font-size: 16px;

    .player-number {
      font-weight: 900;
      display: inline-flex;
      border: 1px solid var(--grey-50);
      aspect-ratio: 1;
      width: 40px;
      justify-content: center;
      align-items: center;
      border-radius: 40px;
    }

    .player-number.front-row {
      border-width: 1px;
      border-color: var(--grey-800);
    }

    .player-number.libero-circle {
      border: 1px solid var(--red-400);
      color: var(--red-400);
    }
  }
}


.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.3);
  z-index: 999;
}

.substitution-modal {
  position: fixed;
  background: var(--white);
  color: var(--grey-900);
  padding: 20px;
  border: 1px solid var(--grey-100);
  border-radius: 12px; /* Rounder border */
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1); /* Subtle shadow with light source from top */
  z-index: 1000;

  h3 {
    margin: 0 0 15px 0;
    font-size: 16px;
    font-weight: 600;
  }

  .player-list {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 12px;
      border-radius: 6px;
      cursor: pointer;
      transition: background-color 0.2s ease;

      &:hover {
        background-color: var(--grey-10);
      }

      .player-number {
        display: inline-flex;
        justify-content: center;
        align-items: center;
        width: 24px;
        height: 24px;
        border: 1px solid var(--grey-50);
        border-radius: 50%;
        font-size: 12px;
        font-weight: 900;
        background-color: var(--white);
      }

      .player-name {
        font-size: 14px;
        font-weight: 600;
        color: var(--grey-900);
      }
    }
  }

  button {
    margin-top: 15px;
    padding: 8px 16px;
    background-color: var(--red-accent);
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 900;
    color: var(--white);
    transition: background-color 0.2s ease;

    &:hover {
      background-color: var(--red-accent-2);
    }
  }

  .switch-role-btn {
    width: 100%;
    background-color: var(--grey-900);
    margin-top: 12px;

    &:hover {
      background-color: var(--grey-1000);
    }
  }
}
</style>